import React, { useState, useEffect } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { HiEye, HiEyeOff, HiArrowLeft, HiCheckCircle,HiExclamationCircle, } from "react-icons/hi";
import routesfe from "../Config/RoutesFE";

// Schema for the Request Code form
const requestSchema = z.object({
  email: z.string().email("Invalid email address"),
});

// Schema for the Reset Password form
const resetSchema = z
  .object({
    verificationCode: z.string().min(1, "Verification code is required"),
    newPassword: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(
        /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
        "Password must be at least 8 characters, include 1 uppercase, 1 lowercase, 1 number, and 1 special character"
      ),
    confirmPassword: z.string().min(1, "Confirm password is required"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

  const CountdownCircle = ({ seconds }) => {
  const radius = 18;
  const stroke = 3;
  const normalizedRadius = radius - stroke * 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset =
    circumference - (seconds / 30) * circumference; // assuming max 30s cooldown

  return (
    <svg height={radius * 2} width={radius * 2} className="mr-2 -ml-1">
      <circle
        stroke="#4F46E5"
        fill="transparent"
        strokeWidth={stroke}
        strokeDasharray={`${circumference} ${circumference}`}
        style={{ strokeDashoffset, transition: "stroke-dashoffset 1s linear" }}
        r={normalizedRadius}
        cx={radius}
        cy={radius}
        className="origin-center"
      />
    </svg>
  );
};

function ForgotPasswordPage() {
  // Local state for managing stages and UI feedback
  const [email, setEmail] = useState("");
  const [stage, setStage] = useState("request");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  const navigate = useNavigate();

  // Resend cooldown timer
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Auto-remove error alert after 10 seconds
  useEffect(() => {
    if (error) {
      setShowSuccessAlert(false);
      const timer = setTimeout(() => {
        setError(null);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // react-hook-form for the Request Code stage
  const {
    control: controlRequest,
    handleSubmit: handleSubmitRequest,
    formState: { errors: requestErrors },
  } = useForm({
    resolver: zodResolver(requestSchema),
    defaultValues: { email: "" },
  });

  // react-hook-form for the Reset Password stage
  const {
    control: controlReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: resetErrors },
  } = useForm({
    resolver: zodResolver(resetSchema),
    defaultValues: {
      verificationCode: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Handler to request a verification code
  const onRequestCode = async (data) => {
    setLoading(true);
    setError(null);
    try {
      await Auth.forgotPassword(data.email);
      setEmail(data.email);
      setStage("reset");
      setShowSuccessAlert(true);
      setResendCooldown(30);
    } catch (err) {
      setError("Error requesting code: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handler to resend the verification code
  const onResendCode = async () => {
    setResendLoading(true);
    setError(null);
    try {
      await Auth.forgotPassword(email);
      setShowSuccessAlert(true);
      setResendCooldown(30);
    } catch (err) {
      setError("Error resending code: " + err.message);
    } finally {
      setResendLoading(false);
    }
  };

  // Handler to reset the password
  const onResetPassword = async (data) => {
    setLoading(true);
    setError(null);
    try {
      await Auth.forgotPasswordSubmit(email, data.verificationCode, data.newPassword);
      setError(null);
      setShowSuccessAlert(false);
      navigate(routesfe.LOGIN);
    } catch (err) {
      setError("Error resetting password: " + err.message);
    } finally {
      setLoading(false);
    }
  };

   return (
    <div className="min-h-screen bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-100 flex items-center justify-center px-4 py-10">
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-8 sm:p-10">

        <h2 className="text-3xl font-extrabold text-indigo-600 text-center mb-6">
          {stage === "request" ? "Forgot Password" : "Reset Password"}
        </h2>

        {/* Show error alert with icon */}
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded px-4 py-3 mb-6">
            <HiExclamationCircle className="w-6 h-6" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {stage === "request" ? (
          <form onSubmit={handleSubmitRequest(onRequestCode)} className="space-y-6" noValidate>
            <div>
              <label htmlFor="email" className="block mb-1 font-medium text-gray-700">
                Email Address
              </label>
              <Controller
                name="email"
                control={controlRequest}
                render={({ field }) => (
                  <input
                    {...field}
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    className={`w-full rounded-md border px-4 py-3 text-base 
                      focus:outline-none focus:ring-2 focus:ring-indigo-500 transition
                      ${requestErrors.email ? "border-red-500" : "border-gray-300"}`}
                    autoComplete="email"
                  />
                )}
              />
              {requestErrors.email && (
                <p className="mt-1 text-red-500 text-sm">{requestErrors.email.message}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 rounded-xl text-white font-semibold transition
                ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-500"}
                focus:outline-none focus:ring-4 focus:ring-indigo-300`}
            >
              {loading ? "Requesting..." : "Request Code"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmitReset(onResetPassword)} className="space-y-6" noValidate>
            {/* Success alert with icon */}
            {showSuccessAlert && (
              <div className="flex items-center gap-3 bg-green-50 border border-green-200 text-green-700 rounded p-3 mb-6 animate-fadeIn">
                <HiCheckCircle className="w-6 h-6" />
                <p className="text-sm">
                  Verification code sent to <strong>{email}</strong>
                </p>
              </div>
            )}

            <div>
              <label htmlFor="verificationCode" className="block mb-1 font-medium text-gray-700">
                Verification Code
              </label>
              <Controller
                name="verificationCode"
                control={controlReset}
                render={({ field }) => (
                  <input
                    {...field}
                    id="verificationCode"
                    type="text"
                    placeholder="Enter code"
                    className={`w-full rounded-md border px-4 py-3 text-base 
                      focus:outline-none focus:ring-2 focus:ring-indigo-500 transition
                      ${resetErrors.verificationCode ? "border-red-500" : "border-gray-300"}`}
                    autoComplete="one-time-code"
                  />
                )}
              />
              {resetErrors.verificationCode && (
                <p className="mt-1 text-red-500 text-sm">{resetErrors.verificationCode.message}</p>
              )}
            </div>

            {/* New Password */}
            <div className="relative">
              <label htmlFor="newPassword" className="block mb-1 font-medium text-gray-700">
                New Password
              </label>
              <Controller
                name="newPassword"
                control={controlReset}
                render={({ field }) => (
                  <input
                    {...field}
                    id="newPassword"
                    type={passwordVisible ? "text" : "password"}
                    placeholder="Enter new password"
                    className={`w-full rounded-md border px-4 py-3 text-base 
                      focus:outline-none focus:ring-2 focus:ring-indigo-500 transition
                      ${resetErrors.newPassword ? "border-red-500" : "border-gray-300"}`}
                    autoComplete="new-password"
                  />
                )}
              />
              <button
                type="button"
                onClick={() => setPasswordVisible(!passwordVisible)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-indigo-600 focus:outline-none"
                aria-label={passwordVisible ? "Hide password" : "Show password"}
              >
                {passwordVisible ? <HiEye size={22} /> : <HiEyeOff size={22} />}
              </button>
              {resetErrors.newPassword && (
                <p className="mt-1 text-red-500 text-sm">{resetErrors.newPassword.message}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="relative">
              <label htmlFor="confirmPassword" className="block mb-1 font-medium text-gray-700">
                Confirm Password
              </label>
              <Controller
                name="confirmPassword"
                control={controlReset}
                render={({ field }) => (
                  <input
                    {...field}
                    id="confirmPassword"
                    type={confirmPasswordVisible ? "text" : "password"}
                    placeholder="Confirm new password"
                    className={`w-full rounded-md border px-4 py-3 text-base 
                      focus:outline-none focus:ring-2 focus:ring-indigo-500 transition
                      ${resetErrors.confirmPassword ? "border-red-500" : "border-gray-300"}`}
                    autoComplete="new-password"
                  />
                )}
              />
              <button
                type="button"
                onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-indigo-600 focus:outline-none"
                aria-label={confirmPasswordVisible ? "Hide confirm password" : "Show confirm password"}
              >
                {confirmPasswordVisible ? <HiEye size={22} /> : <HiEyeOff size={22} />}
              </button>
              {resetErrors.confirmPassword && (
                <p className="mt-1 text-red-500 text-sm">{resetErrors.confirmPassword.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 rounded-xl text-white font-semibold transition shadow-md focus:outline-none focus:ring-4 ${
                loading ? "bg-gray-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-500 focus:ring-indigo-300"
              }`}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>

            {resendCooldown > 0 ? (
              <button
                disabled
                className="w-full mt-3 flex items-center justify-center gap-2 py-2 rounded-md bg-gray-300 text-gray-600 font-semibold"
              >
                <CountdownCircle seconds={resendCooldown} />
                Resend OTP in {resendCooldown}s
              </button>
            ) : (
              <button
                onClick={onResendCode}
                disabled={resendLoading}
                className="w-full mt-3 py-2 rounded-md bg-indigo-600 text-white font-semibold hover:bg-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-300 flex justify-center"
              >
                {resendLoading ? "Resending..." : "Resend OTP"}
              </button>
            )}
          </form>
        )}

        {/* Footer Navigation */}
        <div className="mt-6 flex justify-between text-indigo-600 text-sm font-medium">
          <button
            onClick={() => navigate(routesfe.LANDING)}
            className="hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-400 rounded"
          >
            ← Back to Home
          </button>
          {stage !== "request" && (
            <button
              onClick={() => navigate(routesfe.LOGIN)}
              className="hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-400 rounded"
            >
              Login
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;